import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SEO from '../components/SEO'
import { db } from '../lib/db'
import 'remixicon/fonts/remixicon.css'

export default function Order() {
  const [selectedCategory, setSelectedCategory] = useState('All')

  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null)
    } else {
      setSelectedCategory(category)
    }
  }
  const [cart, setCart] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [selectedSize, setSelectedSize] = useState('Regular')
  const [selectedAddons, setSelectedAddons] = useState([])
  const [specialInstructions, setSpecialInstructions] = useState('')
  const [promoCode, setPromoCode] = useState('')
  const [discount, setDiscount] = useState(0)
  
  const [checkoutStep, setCheckoutStep] = useState(null)
  const [deliveryDetails, setDeliveryDetails] = useState({
    address: '',
    addressType: 'Home',
    contactNumber: '',
    deliveryTime: 'ASAP',
    scheduledTime: '',
    paymentMethod: 'Cash on Delivery'
  })
  const [orderConfirmed, setOrderConfirmed] = useState(false)
  const [orderNumber, setOrderNumber] = useState('')
  const [lastOrderDetails, setLastOrderDetails] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [placeholderText, setPlaceholderText] = useState('')
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [restaurants, setRestaurants] = useState([])
  const [loadingData, setLoadingData] = useState(true)
  const [dataError, setDataError] = useState(null)
  const categoryRef = useRef(null)

  useEffect(() => {
    async function fetchData() {
      try {
        setLoadingData(true)
        const data = await db.getActiveRestaurantsWithMenus()
        setRestaurants(data)
      } catch (err) {
        console.error('Failed to load restaurants:', err)
        setDataError('Failed to load menu. Please refresh the page.')
      } finally {
        setLoadingData(false)
      }
    }
    fetchData()
  }, [])

  const searchPlaceholders = [
    'Search for restaurants...',
    'Search for meals...',
    'Search for cuisines...',
    'Try "Chicken"...',
    'Try "Pizza"...',
    'Try "Burger"...',
    'Try "Nando\'s"...',
    'Try "Peri-Peri"...'
  ]

  // Typewriter effect for placeholder
  useEffect(() => {
    if (isSearchFocused || searchQuery) return

    let currentText = ''
    let currentIndex = 0
    let isDeleting = false
    let timeout

    const type = () => {
      const fullText = searchPlaceholders[placeholderIndex]

      if (!isDeleting) {
        currentText = fullText.substring(0, currentIndex + 1)
        currentIndex++

        if (currentIndex === fullText.length) {
          timeout = setTimeout(() => {
            isDeleting = true
            type()
          }, 2000)
          setPlaceholderText(currentText)
          return
        }
      } else {
        currentText = fullText.substring(0, currentIndex - 1)
        currentIndex--

        if (currentIndex === 0) {
          isDeleting = false
          setPlaceholderIndex((prev) => (prev + 1) % searchPlaceholders.length)
          timeout = setTimeout(type, 500)
          setPlaceholderText(currentText)
          return
        }
      }

      setPlaceholderText(currentText)
      timeout = setTimeout(type, isDeleting ? 50 : 100)
    }

    timeout = setTimeout(type, 200)

    return () => clearTimeout(timeout)
  }, [placeholderIndex, isSearchFocused, searchQuery])

  const addons = [
    { id: 1, name: 'Extra Cheese', price: 1.00 },
    { id: 2, name: 'Bacon', price: 1.50 },
    { id: 3, name: 'Avocado', price: 2.00 },
    { id: 4, name: 'Extra Sauce', price: 0.50 },
    { id: 5, name: 'Jalapeños', price: 0.75 }
  ]

  const categories = ['All', ...new Set(
    restaurants.flatMap(r => 
      r.items.map(item => item.category_name).filter(Boolean)
    )
  )]

  const allItems = restaurants.flatMap(restaurant => 
    restaurant.items.map(item => ({ 
      ...item, 
      price: parseFloat(item.price),
      restaurant 
    }))
  )

  // Search filtering - ignore whitespace-only queries
  const hasValidSearch = searchQuery.trim().length > 0
  const searchFilteredItems = hasValidSearch
    ? allItems.filter(item => {
        const query = searchQuery.trim().toLowerCase()
        return (
          item.name.toLowerCase().includes(query) ||
          (item.description || '').toLowerCase().includes(query) ||
          item.restaurant.name.toLowerCase().includes(query) ||
          (item.restaurant.address || '').toLowerCase().includes(query) ||
          (item.category_name || '').toLowerCase().includes(query) ||
          (item.restaurant.cuisine_type || '').toLowerCase().includes(query)
        )
      })
    : allItems

  // Category filtering
  const filteredItems = hasValidSearch
    ? searchFilteredItems
    : selectedCategory === null || selectedCategory === 'All'
      ? allItems
      : allItems.filter(item => item.category_name === selectedCategory || (item.is_featured && selectedCategory === 'Featured'))

  const groupedByRestaurant = filteredItems.reduce((acc, item) => {
    const restaurantName = item.restaurant.name
    if (!acc[restaurantName]) {
      acc[restaurantName] = {
        restaurant: item.restaurant,
        items: []
      }
    }
    acc[restaurantName].items.push(item)
    return acc
  }, {})

  const addToCart = () => {
    const addonsTotal = selectedAddons.reduce((sum, addon) => sum + addon.price, 0)
    const sizeMultiplier = selectedSize === 'Large' ? 1.5 : 1
    const itemPrice = (selectedItem.price * sizeMultiplier) + addonsTotal
    
    setCart([...cart, { 
      ...selectedItem, 
      restaurantName: selectedItem.restaurant.name,
      restaurantId: selectedItem.restaurant.id,
      restaurantDeliveryFee: parseFloat(selectedItem.restaurant.delivery_fee || 0),
      quantity,
      size: selectedSize,
      addons: selectedAddons,
      specialInstructions,
      itemPrice
    }])
    setSelectedItem(null)
    setQuantity(1)
    setSelectedSize('Regular')
    setSelectedAddons([])
    setSpecialInstructions('')
  }

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index))
  }

  const updateCartQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return
    const updatedCart = [...cart]
    updatedCart[index].quantity = newQuantity
    setCart(updatedCart)
  }

  const toggleAddon = (addon) => {
    if (selectedAddons.find(a => a.id === addon.id)) {
      setSelectedAddons(selectedAddons.filter(a => a.id !== addon.id))
    } else {
      setSelectedAddons([...selectedAddons, addon])
    }
  }

  const applyPromoCode = () => {
    if (promoCode.toUpperCase() === 'AMIBITES10') {
      setDiscount(subtotal * 0.1)
    } else if (promoCode.toUpperCase() === 'WELCOME20') {
      setDiscount(subtotal * 0.2)
    } else {
      alert('Invalid promo code')
    }
  }

  const proceedToCheckout = () => {
    setCheckoutStep('delivery')
  }

  const WHATSAPP_BUSINESS_NUMBER = '16838886864'

  const sendWhatsAppOrderNotification = (orderNum, orderItems, orderTotal, delivery, restaurantName) => {
    const itemsList = orderItems.map(item => `  - ${item.quantity}x ${item.name}`).join('\n')
    const message = [
      `Ami Bites Website: Order placed`,
      `Order ${orderNum}`,
      `${restaurantName} | $${orderTotal.toFixed(2)} COD`,
      `Delivery: ${delivery.address || 'N/A'}`,
      `Customer: ${delivery.contactNumber}`,
      `Items:\n${itemsList}`,
      `Status: Order received`
    ].join('\n')

    const whatsappUrl = `https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  const sendFormspreeOrderNotification = async (orderNum, orderItems, orderTotal, delivery, restaurantName) => {
    const itemsList = orderItems.map(item => `${item.quantity}x ${item.name} @ $${item.itemPrice.toFixed(2)} = $${(item.quantity * item.itemPrice).toFixed(2)}`).join('\n')
    const formData = {
      _subject: `New Order: ${orderNum} - ${restaurantName}`,
      orderNumber: orderNum,
      restaurant: restaurantName,
      items: itemsList,
      subtotal: `$${(orderTotal - deliveryFee - serviceFee + discount).toFixed(2)}`,
      deliveryFee: `$${deliveryFee.toFixed(2)}`,
      serviceFee: `$${serviceFee.toFixed(2)}`,
      discount: discount > 0 ? `-$${discount.toFixed(2)}` : '$0.00',
      totalAmount: `$${orderTotal.toFixed(2)}`,
      paymentMethod: 'Cash on Delivery',
      deliveryAddress: delivery.address || 'N/A',
      customerPhone: delivery.contactNumber || 'N/A',
      orderDate: new Date().toLocaleString('en-ZW', { timeZone: 'Africa/Harare' }),
      status: 'Order Received'
    }

    try {
      await fetch('https://formspree.io/f/mkoabzka', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
    } catch (err) {
      console.error('Formspree notification failed:', err)
    }
  }

  const handleTrackOrder = () => {
    const message = `Hi Ami Bites, I'd like to track my order.\nOrder Number: ${orderNumber}\nPlease update me on the status.`
    const whatsappUrl = `https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  const handleContactSupport = () => {
    const message = orderNumber
      ? `Hi Ami Bites, I need help with my order ${orderNumber}.`
      : `Hi Ami Bites, I need assistance with my order.`
    const whatsappUrl = `https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  const confirmOrder = async () => {
    const orderItems = cart.map(item => ({ name: item.name, quantity: item.quantity, itemPrice: item.itemPrice }))
    const restaurantName = cart[0]?.restaurantName || 'Restaurant'
    const orderTotal = total

    try {
      const orderNum = 'AMI' + Math.random().toString(36).substr(2, 9).toUpperCase()
      
      const restaurantId = cart[0]?.restaurantId || null
      const order = await db.createOrder({
        restaurant_id: restaurantId,
        subtotal,
        delivery_fee: deliveryFee,
        total,
        delivery_address: deliveryDetails.address,
        customer_name: deliveryDetails.contactNumber,
        customer_phone: deliveryDetails.contactNumber,
        special_instructions: cart.map(i => i.specialInstructions).filter(Boolean).join('; ')
      })

      if (order) {
        await db.createOrderItems(order.id, cart.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          unit_price: item.itemPrice,
          subtotal: item.itemPrice * item.quantity
        })))
      }

      setOrderNumber(orderNum)
      setLastOrderDetails({ items: orderItems, restaurantName, total: orderTotal, delivery: deliveryDetails })
      setOrderConfirmed(true)
      setCheckoutStep(null)
      setCart([])

      // Send WhatsApp notification to business
      sendWhatsAppOrderNotification(orderNum, orderItems, orderTotal, deliveryDetails, restaurantName)
      // Send email notification via Formspree
      sendFormspreeOrderNotification(orderNum, orderItems, orderTotal, deliveryDetails, restaurantName)
    } catch (err) {
      console.error('Order failed:', err)
      const orderNum = 'AMI' + Math.random().toString(36).substr(2, 9).toUpperCase()
      setOrderNumber(orderNum)
      setLastOrderDetails({ items: orderItems, restaurantName, total: orderTotal, delivery: deliveryDetails })
      setOrderConfirmed(true)
      setCheckoutStep(null)
      setCart([])

      // Still send WhatsApp notification even if DB failed
      sendWhatsAppOrderNotification(orderNum, orderItems, orderTotal, deliveryDetails, restaurantName)
      // Still send email notification even if DB failed
      sendFormspreeOrderNotification(orderNum, orderItems, orderTotal, deliveryDetails, restaurantName)
    }
  }

  const subtotal = cart.reduce((sum, item) => sum + (item.itemPrice * item.quantity), 0)
  const deliveryFee = cart.length > 0 ? Math.max(...cart.map(i => i.restaurantDeliveryFee || 0)) : 0
  const serviceFee = cart.length > 0 ? 1.00 : 0
  const total = subtotal + deliveryFee + serviceFee - discount

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50">
      <SEO 
        title="Order Food Online - Browse Restaurants & Menus | Ami Bites Zimbabwe"
        description="Order food online from top restaurants in Zimbabwe. Browse menus from Nando's, KFC, Pizza Inn, Chicken Inn & more. Fast delivery to Mt Pleasant, Avondale, Pomona. Order now!"
        keywords="order food online Zimbabwe, food delivery Harare, Nando's delivery, KFC delivery, Pizza Inn delivery, restaurant menu Zimbabwe, online food ordering, food delivery near me"
        canonical="https://amibites.com/order"
      />
      {/* Top Navigation Bar */}
      <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col gap-4 items-center">
            <div className="relative w-full max-w-2xl group">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder={placeholderText}
                className="w-full pl-4 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all duration-300 focus:shadow-lg focus:shadow-orange-100"
              />
              
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <AnimatePresence>
                  {searchQuery && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0 }}
                      onClick={() => setSearchQuery('')}
                      className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                    >
                      <i className="ri-close-line text-sm text-gray-600"></i>
                    </motion.button>
                  )}
                </AnimatePresence>
                
                <motion.div
                  animate={{
                    scale: isSearchFocused ? 1.05 : 1,
                    rotate: isSearchFocused ? [0, -10, 10, 0] : 0
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <i className={`ri-search-line text-xl transition-colors duration-300 ${
                    isSearchFocused ? 'text-orange-500' : 'text-gray-400'
                  }`}></i>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Category Tabs - Sticky Scroll */}
          <div ref={categoryRef} className="flex gap-3 mt-4 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`px-6 py-2 rounded-full font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Results Indicator */}
        {hasValidSearch && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center justify-between bg-gradient-to-r from-orange-50 to-green-50 p-4 rounded-2xl border-2 border-orange-200"
          >
            <div className="flex items-center gap-3">
              <i className="ri-search-2-line text-2xl text-orange-600"></i>
              <div>
                <h3 className="font-bold text-gray-900">
                  Search Results for "{searchQuery.trim()}"
                </h3>
                <p className="text-sm text-gray-600">
                  Found {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSearchQuery('')}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
            >
              <i className="ri-close-line text-lg"></i>
              <span className="text-sm font-medium">Clear</span>
            </button>
          </motion.div>
        )}

        {/* Loading State */}
        {loadingData && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">Loading restaurants...</p>
          </div>
        )}

        {/* Error State */}
        {dataError && !loadingData && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center mb-8">
            <i className="ri-error-warning-line text-4xl text-red-400 mb-2"></i>
            <p className="text-red-600 font-medium">{dataError}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {!loadingData && !dataError && (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Restaurant & Menu Items Section */}
          <div className="lg:col-span-2 space-y-8">
            {Object.values(groupedByRestaurant).length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-3xl shadow-xl p-12 text-center"
              >
                <i className="ri-search-eye-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600 mb-6">
                  We couldn't find any items matching "{searchQuery}"
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold hover:shadow-lg transition-shadow"
                >
                  Clear Search
                </button>
              </motion.div>
            ) : (
              Object.values(groupedByRestaurant).map(({ restaurant, items }) => (
              <motion.div
                key={restaurant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl shadow-xl overflow-hidden"
              >
                {/* Restaurant Header Section */}
                <div className="relative h-48 overflow-hidden bg-gradient-to-br from-orange-400 to-green-400">
                  {restaurant.image_url ? (
                    <img 
                      src={restaurant.image_url} 
                      alt={restaurant.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-restaurant-2-line text-6xl text-white/50"></i>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
                    <h3 className="text-2xl sm:text-3xl font-bold mb-2">{restaurant.name}</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                      {restaurant.rating > 0 && (
                        <span className="flex items-center bg-white/20 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full">
                          <i className="ri-star-fill text-xs sm:text-base text-yellow-400 mr-1"></i>
                          {parseFloat(restaurant.rating).toFixed(1)}
                        </span>
                      )}
                      {restaurant.delivery_time && (
                        <span className="flex items-center">
                          <i className="ri-time-line text-xs sm:text-base mr-1"></i>
                          {restaurant.delivery_time}
                        </span>
                      )}
                      {restaurant.delivery_fee && (
                        <span className="flex items-center">
                          ${parseFloat(restaurant.delivery_fee).toFixed(2)} Delivery
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs sm:text-sm">
                      {restaurant.address && (
                        <>
                          <i className="ri-map-pin-line text-xs sm:text-base"></i>
                          <span>{restaurant.address}</span>
                        </>
                      )}
                      {restaurant.cuisine_type && (
                        <span className="px-2 py-1 rounded-full text-xs font-bold bg-white/20 backdrop-blur-sm">
                          {restaurant.cuisine_type}
                        </span>
                      )}
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-500">
                        Open
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-3 bg-gray-50 border-b overflow-x-auto">
                  <button className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap">
                    <i className="ri-phone-line text-base"></i>
                    <span className="text-xs sm:text-sm font-medium">Call</span>
                  </button>
                  <button className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap">
                    <i className="ri-message-2-line text-base"></i>
                    <span className="text-xs sm:text-sm font-medium">Reviews</span>
                  </button>
                  <button className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap">
                    <i className="ri-share-line text-base"></i>
                    <span className="text-xs sm:text-sm font-medium">Share</span>
                  </button>
                </div>

                {/* Menu Item Cards */}
                <div className="p-4 sm:p-6 space-y-4">
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      whileHover={{ scale: 1.02 }}
                      className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-orange-50 to-green-50 rounded-2xl cursor-pointer group"
                      onClick={() => setSelectedItem(item)}
                    >
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl flex-shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-orange-200 to-green-200 rounded-xl flex-shrink-0 flex items-center justify-center">
                          <i className="ri-restaurant-line text-2xl text-orange-500"></i>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h4 className="font-bold text-base sm:text-lg text-gray-900">{item.name}</h4>
                              {item.is_featured && (
                                <span className="px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                                  FEATURED
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-xs sm:text-sm mb-2 line-clamp-2">{item.description}</p>
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span className="text-lg sm:text-xl font-bold text-orange-600">${item.price.toFixed(2)}</span>
                              {item.category_name && (
                                <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                  {item.category_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <button className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                            <i className="ri-add-line text-base sm:text-xl"></i>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              ))
            )}
          </div>

          {/* Cart Sidebar */}
          <div className="lg:sticky lg:top-32 h-fit">
            <div className="bg-white rounded-3xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">Your Cart</h3>
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-full flex items-center justify-center font-bold">
                  {cart.length}
                </div>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-shopping-cart-line text-6xl text-gray-300 mx-auto mb-4"></i>
                  <p className="text-gray-500 font-medium">Your cart is empty</p>
                  <p className="text-sm text-gray-400 mt-2">Add items to get started</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                    {cart.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="p-3 bg-gray-50 rounded-xl"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm">{item.name}</h4>
                            <p className="text-xs text-gray-500">{item.restaurantName}</p>
                            {item.size !== 'Regular' && (
                              <p className="text-xs text-orange-600">Size: {item.size}</p>
                            )}
                            {item.addons.length > 0 && (
                              <p className="text-xs text-gray-500">
                                + {item.addons.map(a => a.name).join(', ')}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => removeFromCart(index)}
                            className="w-8 h-8 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center"
                          >
                            <i className="ri-close-line text-base"></i>
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => updateCartQuantity(index, item.quantity - 1)}
                              className="w-7 h-7 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors flex items-center justify-center"
                            >
                              <i className="ri-subtract-line text-xs"></i>
                            </button>
                            <span className="text-sm font-bold w-6 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateCartQuantity(index, item.quantity + 1)}
                              className="w-7 h-7 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center"
                            >
                              <i className="ri-add-line text-xs"></i>
                            </button>
                          </div>
                          <span className="text-sm font-bold text-orange-600">
                            ${(item.itemPrice * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Promo Code */}
                  <div className="mb-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        placeholder="Enter promo code"
                        className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none text-sm"
                      />
                      <button
                        onClick={applyPromoCode}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                      >
                        Apply
                      </button>
                    </div>
                  </div>

                  {/* Cart Summary */}
                  <div className="border-t pt-4 space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Delivery Fee</span>
                      <span className="font-semibold">${deliveryFee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Service Fee</span>
                      <span className="font-semibold">${serviceFee.toFixed(2)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span className="font-semibold">-${discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span className="bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">Total</span>
                      <span className="bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={proceedToCheckout}
                    className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-shadow"
                  >
                    Proceed to Checkout
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Item Customization Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">{selectedItem.name}</h3>
                  <p className="text-gray-600">{selectedItem.restaurant.name}</p>
                </div>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {selectedItem.image_url ? (
                <img 
                  src={selectedItem.image_url} 
                  alt={selectedItem.name}
                  className="w-full h-48 object-cover rounded-2xl mb-4"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-orange-100 to-green-100 rounded-2xl mb-4 flex items-center justify-center">
                  <i className="ri-restaurant-line text-5xl text-orange-300"></i>
                </div>
              )}

              <p className="text-gray-700 mb-4">{selectedItem.description}</p>
              <div className="flex items-center space-x-4 mb-6">
                <span className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">
                  ${selectedItem.price.toFixed(2)}
                </span>
                {selectedItem.category_name && (
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {selectedItem.category_name}
                  </span>
                )}
              </div>

              {/* Size Selection */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  {['Regular', 'Large'].map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(selectedSize === size ? 'Regular' : size)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        selectedSize === size
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold">{size}</div>
                      {size === 'Large' && (
                        <div className="text-xs text-gray-500">+50% price</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Add-ons</h4>
                <div className="space-y-2">
                  {addons.map((addon) => (
                    <button
                      key={addon.id}
                      onClick={() => toggleAddon(addon)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                        selectedAddons.find(a => a.id === addon.id)
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium">{addon.name}</span>
                      <span className="text-orange-600 font-semibold">+${addon.price.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Special Instructions */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3">Special Instructions</h4>
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Add notes for restaurant..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none resize-none"
                  rows="3"
                />
              </div>

              {/* Quantity */}
              <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-xl">
                <span className="font-semibold">Quantity</span>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center hover:bg-orange-600 transition-colors"
                  >
                    <i className="ri-subtract-line text-xl"></i>
                  </button>
                  <span className="text-xl font-bold w-8 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                  >
                    <i className="ri-add-line text-xl"></i>
                  </button>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={addToCart}
                className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg shadow-lg"
              >
                Add to Cart - ${((selectedItem.price * (selectedSize === 'Large' ? 1.5 : 1) + selectedAddons.reduce((sum, a) => sum + a.price, 0)) * quantity).toFixed(2)}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Checkout Flow Modal */}
      <AnimatePresence>
        {checkoutStep && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setCheckoutStep(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-green-600 bg-clip-text text-transparent">
                  Checkout
                </h2>
                <button
                  onClick={() => setCheckoutStep(null)}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Step Indicators */}
              <div className="flex items-center justify-between mb-8">
                {['delivery', 'time', 'payment'].map((step, index) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      checkoutStep === step ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {index + 1}
                    </div>
                    {index < 2 && <div className="w-20 h-1 bg-gray-200 mx-2" />}
                  </div>
                ))}
              </div>

              {/* Step 1: Delivery Details */}
              {checkoutStep === 'delivery' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold">Delivery Details</h3>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Delivery Address</label>
                    <div className="flex items-center gap-2 mb-3">
                      <button 
                        onClick={() => setDeliveryDetails({...deliveryDetails, addressType: deliveryDetails.addressType === 'Home' ? '' : 'Home'})}
                        className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg transition-colors ${
                          deliveryDetails.addressType === 'Home'
                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <i className="ri-home-4-line text-base"></i>
                        <span className="text-sm font-medium">Home</span>
                      </button>
                      <button 
                        onClick={() => setDeliveryDetails({...deliveryDetails, addressType: deliveryDetails.addressType === 'Work' ? '' : 'Work'})}
                        className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg transition-colors ${
                          deliveryDetails.addressType === 'Work'
                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <i className="ri-building-line text-base"></i>
                        <span className="text-sm font-medium">Work</span>
                      </button>
                    </div>
                    <textarea
                      value={deliveryDetails.address}
                      onChange={(e) => setDeliveryDetails({...deliveryDetails, address: e.target.value})}
                      placeholder="Enter your delivery address..."
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none resize-none"
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Contact Number</label>
                    <input
                      type="tel"
                      value={deliveryDetails.contactNumber}
                      onChange={(e) => setDeliveryDetails({...deliveryDetails, contactNumber: e.target.value})}
                      placeholder="07X XXX XXXX"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
                    />
                  </div>

                  <button
                    onClick={() => setCheckoutStep('time')}
                    className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg"
                  >
                    Continue
                  </button>
                </div>
              )}

              {/* Step 2: Delivery Time */}
              {checkoutStep === 'time' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold">Delivery Time</h3>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => setDeliveryDetails({...deliveryDetails, deliveryTime: deliveryDetails.deliveryTime === 'ASAP' ? '' : 'ASAP'})}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        deliveryDetails.deliveryTime === 'ASAP'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <i className="ri-time-line text-xl text-orange-600"></i>
                        <div className="text-left">
                          <div className="font-semibold">ASAP</div>
                          <div className="text-sm text-gray-500">Delivery in 30-45 mins</div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setDeliveryDetails({...deliveryDetails, deliveryTime: deliveryDetails.deliveryTime === 'Scheduled' ? '' : 'Scheduled'})}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        deliveryDetails.deliveryTime === 'Scheduled'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <i className="ri-calendar-line text-xl text-orange-600"></i>
                        <div className="text-left">
                          <div className="font-semibold">Schedule for later</div>
                          <div className="text-sm text-gray-500">Choose a specific time</div>
                        </div>
                      </div>
                    </button>

                    {deliveryDetails.deliveryTime === 'Scheduled' && (
                      <input
                        type="datetime-local"
                        value={deliveryDetails.scheduledTime}
                        onChange={(e) => setDeliveryDetails({...deliveryDetails, scheduledTime: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
                      />
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setCheckoutStep('delivery')}
                      className="flex-1 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setCheckoutStep('payment')}
                      className="flex-1 py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment Options */}
              {checkoutStep === 'payment' && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold">Payment Method</h3>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => setDeliveryDetails({...deliveryDetails, paymentMethod: 'Cash on Delivery'})}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        deliveryDetails.paymentMethod === 'Cash on Delivery'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <i className="ri-money-dollar-circle-line text-xl text-orange-600"></i>
                        <div className="text-left">
                          <div className="font-semibold">Cash on Delivery</div>
                          <div className="text-sm text-gray-500">Pay when you receive your order</div>
                        </div>
                      </div>
                      {deliveryDetails.paymentMethod === 'Cash on Delivery' && (
                        <i className="ri-checkbox-circle-fill text-xl text-orange-500"></i>
                      )}
                    </button>

                    {[
                      { icon: 'ri-bank-card-line', label: 'Debit/Credit Card', desc: 'Visa, Mastercard' },
                      { icon: 'ri-smartphone-line', label: 'Mobile Money', desc: 'EcoCash, OneMoney' },
                      { icon: 'ri-wallet-3-line', label: 'Ami Wallet', desc: 'Use your wallet balance' }
                    ].map(({ icon, label, desc }) => (
                      <div
                        key={label}
                        className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                      >
                        <div className="flex items-center gap-3">
                          <i className={`${icon} text-xl text-gray-400`}></i>
                          <div className="text-left">
                            <div className="font-semibold text-gray-500">{label}</div>
                            <div className="text-sm text-gray-400">{desc}</div>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-orange-500 bg-orange-50 px-2 py-1 rounded-full">
                          Coming Soon
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-orange-50 p-4 rounded-xl">
                    <h4 className="font-semibold mb-2">Order Summary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Subtotal</span>
                        <span>${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Delivery Fee</span>
                        <span>${deliveryFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Service Fee</span>
                        <span>${serviceFee.toFixed(2)}</span>
                      </div>
                      {discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span>-${discount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-orange-200">
                        <span>Total</span>
                        <span>${total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setCheckoutStep('time')}
                      className="flex-1 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={confirmOrder}
                      className="flex-1 py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg"
                    >
                      Confirm Order
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Order Confirmation Modal */}
      <AnimatePresence>
        {orderConfirmed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <i className="ri-checkbox-circle-line text-5xl text-green-600"></i>
              </motion.div>

              <h2 className="text-3xl font-bold mb-2">🎉 Order Confirmed!</h2>
              <p className="text-gray-600 mb-6">Your delicious food is on its way</p>

              <div className="bg-orange-50 p-6 rounded-2xl mb-6">
                <div className="text-sm text-gray-600 mb-1">Order Number</div>
                <div className="text-2xl font-bold text-orange-600 mb-4">{orderNumber}</div>
                
                <div className="text-sm text-gray-600 mb-1">Estimated Delivery Time</div>
                <div className="text-lg font-semibold">30-45 minutes</div>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={handleTrackOrder}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  Track Order via WhatsApp
                </button>
                <button 
                  onClick={() => setOrderConfirmed(false)}
                  className="w-full py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-50"
                >
                  Continue Shopping
                </button>
                <button 
                  onClick={handleContactSupport}
                  className="w-full py-3 text-orange-600 font-medium hover:underline flex items-center justify-center gap-1"
                >
                  <i className="ri-whatsapp-line"></i>
                  Contact Support
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  )
}
